/**
 * Search global (Cmd+K / Ctrl+K) — PR #63.
 *
 * Modal de busca rápida invocável de qualquer página. Procura por:
 *   1. Protocolo curto (últimos 6 chars do UUID) via /casos/protocolo/{p}
 *
 * Resultado leva pro /resultado/{caseId} (página descritiva do caso).
 *
 * Listener global instalado uma vez via useEffect quando montado no App.tsx.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Search, X, ArrowRight, AlertCircle } from "lucide-react";

import api from "@/lib/api";
import { useModalA11y } from "@/lib/useModalA11y";

interface ResultadoBusca {
  casoId: string;
  vertical: string;
  etapa: string;
  protocolo: string;
}

export default function GlobalSearch() {
  const [, navigate] = useLocation();
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState("");
  const [resultado, setResultado] = useState<ResultadoBusca | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useModalA11y({ isOpen: aberto, onClose: () => setAberto(false), modalRef, initialFocusRef: inputRef });

  // Listener global Cmd+K / Ctrl+K
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAberto(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset state ao abrir/fechar
  useEffect(() => {
    if (aberto) {
      setTermo("");
      setResultado(null);
      setErro(null);
    }
  }, [aberto]);

  // Busca quando termo tem 4+ chars (sem debounce complicado)
  useEffect(() => {
    if (!aberto || termo.trim().length < 4) {
      setResultado(null);
      setErro(null);
      return;
    }
    const timer = window.setTimeout(async () => {
      setBuscando(true);
      setErro(null);
      try {
        // Protocolo é últimos 6 chars do UUID em UPPER; user digita parcial
        const r = await api.casoPorProtocolo(termo.trim().toUpperCase());
        setResultado({
          casoId: r.casoId,
          vertical: r.vertical,
          etapa: r.etapa,
          protocolo: r.casoId.slice(-6).toUpperCase(),
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes("404")) {
          setErro("Nenhum caso encontrado com esse protocolo.");
        } else {
          setErro(`Erro ao buscar: ${msg.slice(0, 100)}`);
        }
        setResultado(null);
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [termo, aberto]);

  if (!aberto) return null;

  function abrirCaso() {
    if (resultado) {
      setAberto(false);
      navigate(`/resultado/${resultado.casoId}`);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center p-4 pt-[15vh]"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) setAberto(false);
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="global-search-title"
    >
      <div
        ref={modalRef}
        className="w-full max-w-xl rounded-2xl overflow-hidden"
        style={{ background: "oklch(0.14 0.04 240)", border: "1px solid rgba(255,255,255,0.1)" }}
      >
        {/* Header com input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8">
          <Search className="w-5 h-5 text-cyan-400 shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            id="global-search-title"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && resultado) abrirCaso();
            }}
            placeholder="Digite o protocolo (ex: A1B2C3)..."
            aria-label="Buscar caso por protocolo"
            className="flex-1 bg-transparent text-white text-base outline-none placeholder-white/30"
          />
          <kbd className="hidden md:inline-block text-[10px] text-white/40 font-mono px-1.5 py-0.5 rounded border border-white/10">
            ESC
          </kbd>
          <button
            type="button"
            onClick={() => setAberto(false)}
            aria-label="Fechar busca"
            className="p-1 rounded text-white/40 hover:text-white"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 min-h-[120px]">
          {termo.trim().length < 4 && (
            <div className="text-center text-white/40 text-sm py-6">
              Digite ao menos 4 caracteres do protocolo para buscar.
              <p className="text-[11px] mt-2 text-white/30">
                Atalho: <kbd className="font-mono px-1 py-0.5 rounded border border-white/10">Ctrl+K</kbd> em qualquer página
              </p>
            </div>
          )}

          {buscando && (
            <div className="text-center text-cyan-400 text-sm py-6">
              Buscando...
            </div>
          )}

          {erro && !buscando && (
            <div role="alert" className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-red-300">{erro}</p>
            </div>
          )}

          {resultado && !buscando && (
            <button
              type="button"
              onClick={abrirCaso}
              className="w-full flex items-center gap-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/15 transition-colors text-left focus-visible:ring-2 focus-visible:ring-cyan-400"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-cyan-400 text-sm font-bold">
                    #{resultado.protocolo}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/60 capitalize">
                    {resultado.vertical}
                  </span>
                </div>
                <p className="text-xs text-white/70 truncate">
                  Etapa atual: <span className="text-white/90 font-mono">{resultado.etapa}</span>
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-cyan-400 shrink-0" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
